/**
 * The electrics of a room, as plain boxes: the lamp that hangs where its light is, the
 * switch on the wall, and the cord joining them.
 *
 * The cord exists for a gameplay reason rather than a decorative one. A player walking
 * into an unfamiliar room needs to know what a switch will turn off *before* pressing
 * it, and a line running from the plate to a specific lamp says so without a label.
 *
 * The switch is the only interactive fixture. Its id is the room's id — one switch per
 * room, so they are the same thing — which is what lets `availableAction` look a room's
 * light state up directly from the focus target.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import type { BuiltLevel } from './buildGeometry';
import type { Lighting, RoomLight } from './buildLighting';
import { wallSpan, type Box, type LightSwitchSpec, type Rect } from './types';
import { WALL_THICKNESS } from './buildWalls';

/** Light switches sit at hand height in every house ever built. */
export const SWITCH_HEIGHT = 1.15;
/** A switch plate is square and small; this is its side. */
export const SWITCH_SIZE = 0.11;
const SWITCH_PROUD = 0.035;

const CORD_THICKNESS = 0.03;
/** The lamp itself: a shade hanging where the light is. */
const FITTING_WIDTH = 0.34;
const FITTING_HEIGHT = 0.2;

export type Fixture = { roomId: string; box: Box };

export type BuiltFixtures = {
  /** One per room, and the only interactive fixture. Its id is its room's id. */
  switches: Fixture[];
  fittings: Fixture[];
  cords: Fixture[];
};

/**
 * Where a switch goes when the profile does not say. Beside the room's longest wall at
 * a quarter of its run — near where a doorway usually is, and never in a corner where
 * the plate would clip the neighbouring wall.
 */
function defaultSwitch(rect: Rect): LightSwitchSpec {
  return rect.width >= rect.depth
    ? { wall: 'north', offset: rect.width * 0.25 }
    : { wall: 'west', offset: rect.depth * 0.25 };
}

/** The plate's centre, pushed just off the wall face into the room. */
function switchBox(rect: Rect, spec: LightSwitchSpec): Box {
  const span = wallSpan(rect, spec.wall);
  const along = span.from + spec.offset;
  // Which way the room lies from this wall.
  const inward = spec.wall === 'west' || spec.wall === 'north' ? 1 : -1;
  const off = span.line + inward * (WALL_THICKNESS + SWITCH_PROUD / 2);

  return span.axis === 'x'
    ? {
        center: { x: off, y: SWITCH_HEIGHT, z: along },
        size: { x: SWITCH_PROUD, y: SWITCH_SIZE, z: SWITCH_SIZE },
      }
    : {
        center: { x: along, y: SWITCH_HEIGHT, z: off },
        size: { x: SWITCH_SIZE, y: SWITCH_SIZE, z: SWITCH_PROUD },
      };
}

/** A box spanning two points, given a thickness for the other two axes. */
function cordBox(from: [number, number, number], to: [number, number, number]): Box {
  const centre = from.map((value, axis) => (value + to[axis]!) / 2) as [number, number, number];
  const size = from.map((value, axis) =>
    Math.max(Math.abs(to[axis]! - value), CORD_THICKNESS),
  ) as [number, number, number];

  return {
    center: { x: centre[0], y: centre[1], z: centre[2] },
    size: { x: size[0], y: size[1], z: size[2] },
  };
}

/**
 * Three straight runs rather than a curve: up the wall from the switch, across below
 * the ceiling, then down to the lamp. Boxes, so they instance with the trim.
 */
function cordRuns(roomId: string, plate: Box, light: RoomLight, ceiling: number): Fixture[] {
  const riseTo = ceiling - CORD_THICKNESS;
  const start: [number, number, number] = [plate.center.x, plate.center.y, plate.center.z];

  return [
    { roomId, box: cordBox(start, [plate.center.x, riseTo, plate.center.z]) },
    {
      roomId,
      box: cordBox(
        [plate.center.x, riseTo, plate.center.z],
        [light.position.x, riseTo, light.position.z],
      ),
    },
    {
      roomId,
      box: cordBox(
        [light.position.x, riseTo, light.position.z],
        [light.position.x, light.position.y, light.position.z],
      ),
    },
  ];
}

export function buildFixtures(built: BuiltLevel, lighting: Lighting): BuiltFixtures {
  const ceiling = built.ceilings[0] ? built.ceilings[0].box.center.y : 0;
  const switches: Fixture[] = [];
  const fittings: Fixture[] = [];
  const cords: Fixture[] = [];

  for (const light of lighting.lights) {
    const room = built.rooms.find((candidate) => candidate.id === light.roomId);
    if (!room) continue;

    const plate = switchBox(room.rect, room.lightSwitch ?? defaultSwitch(room.rect));
    switches.push({ roomId: light.roomId, box: plate });

    fittings.push({
      roomId: light.roomId,
      box: {
        center: { x: light.position.x, y: light.position.y, z: light.position.z },
        size: { x: FITTING_WIDTH, y: FITTING_HEIGHT, z: FITTING_WIDTH },
      },
    });

    cords.push(...cordRuns(light.roomId, plate, light, ceiling));
  }

  return { switches, fittings, cords };
}
